<template>
  <div>
    <multiselect
      v-model="stores"
      :options="async (q) => loadStoreOptions(q, stores)"
      :loading="loadingStoreOptions"
      :form-label="$t('store.label')"
      :filter-results="false"
      mode="tags"
      :placeholder="$t('store.all')"
      searchable
    >
      <template #tag="{ option, handleTagRemove }">
        <span v-if="option" class="multiselect-tag bg-primary">
          {{ option.name }}
          <span class="multiselect-tag-remove" @click="handleTagRemove(option, $event)">
            <span class="multiselect-tag-remove-icon" />
          </span>
        </span>
      </template>
    </multiselect>
    <fancy-input v-model="start" type="datetime-local" :label="$t('revenueStatistics.startDate')" />
    <fancy-input v-model="end" type="datetime-local" :label="$t('revenueStatistics.endDate')" />
    <simple-dropdown v-model="groupFormat" :label="$t('revenueStatistics.groupFormat.label')">
      <option v-for="(data, key) in groupFormats" :key="key" :value="data">
        {{ $t(`revenueStatistics.groupFormat.${key}`) }}
      </option>
    </simple-dropdown>
    <simple-dropdown v-model="customerGroupFormat" :label="$t('revenueStatistics.customerGroupingMode.label')">
      <option v-for="(key) in customerGroupFormats" :key="key" :value="key">
        {{ $t(`${key}.label`) }}
      </option>
    </simple-dropdown>
    <button class="btn mt-5" @click="fetchData">
      {{ $t('overallStatistics.fetch') }}
    </button>
    <hr>
    <div v-if="statsData">
      <table>
        <thead>
          <tr>
            <th />
            <th />
            <th />
            <th />
            <th v-for="currency in allCurrencies" :key="currency" :colspan="allVatRates.size">
              {{ currency }}
            </th>
          </tr>
          <tr>
            <th />
            <th>{{ $t(`${lastFetchGroupFormat}.label`) }}</th>
            <th>{{ $t('promotion.label') }}</th>
            <th>{{ $t('discount.label') }}</th>
            <template v-for="currency in allCurrencies" :key="currency">
              <th v-for="vat in allVatRates" :key="`${currency}-${vat}`">
                {{ vat * 100 }}%
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <template v-for="(slotData, slot) in statsData" :key="slot">
            <tr>
              <td :rowspan="slotData.rowCount + 1" class="border-t-2 border-black">
                {{ slot }}
              </td>
            </tr>
            <template v-for="(cgData, cgId) in slotData.entries" :key="`${slot}:${cgId}`">
              <tr v-for="(data, key, idxCg) in cgData.entries" :key="`${slot}:${cgId}:${key}`" :class="{'border-t-2 border-black': idxCg === 0}">
                <td v-if="idxCg === 0" :rowspan="cgData.rowCount" class="bg-white">
                  {{ customerGroups[cgId]?.name ?? cgId }}
                </td>
                <td>{{ promotions[data.promotion]?.name }}</td>
                <td>{{ discounts[data.discount]?.name }}</td>
                <template v-for="currency in allCurrencies" :key="currency">
                  <td v-for="vat in allVatRates" :key="`${key}-${currency}-${vat}`" class="text-right">
                    {{
                      formatPrice({
                        amount: data.values[currency]?.[vat] || 0,
                        currency,
                      })
                    }}
                  </td>
                </template>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
      <!-- <pre>
        {{ statsData }}
      </pre> -->
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import {
  subDays, format, formatISO, parse, startOfMonth,
} from 'date-fns';
import useLoadOptions from '@/hooks/api/loadOptions';
import Multiselect from '@/components/utils/forms/Multiselect.vue';
import FancyInput from '@/components/utils/forms/FancyInput.vue';
import SimpleDropdown from '@/components/utils/forms/SimpleDropdown.vue';

import axios from '@/services/axios';
import formatPrice from '@/utils/formatPrice';
import getIdFromIri from '@/utils/getIdFromIri';
import StatisticsTable from '@/components/pages/statistics/overall_statistics/statisticsTable.vue';

const GROUP_BY_CUSTOMER = 'customer';
const GROUP_BY_CUSTOMER_GRPUP = 'customerGroup';

export default {
  name: 'PromotionStatisticsIndexPage',
  components: {
    StatisticsTable,
    Multiselect,
    FancyInput,
    SimpleDropdown,
  },
  setup() {
    const stores = ref([]);
    const start = ref(format(startOfMonth(new Date()), 'yyyy-MM-dd\'T\'HH:mm'));
    const end = ref(format(subDays(new Date(), 1).setHours(23, 59, 59), 'yyyy-MM-dd\'T\'HH:mm'));

    const vats = ref([]);
    const timeSlots = ref([]);
    const statsData = ref(null);
    const discounts = ref({});
    const promotions = ref({});
    const customerGroups = ref({});
    const allCurrencies = ref([]);
    const allVatRates = ref([]);

    const groupFormats = {
      second: {
        backend: '%Y-%m-%dT%H:%i:%s+0100',
        chart: 'yyyy-MM-dd\'T\'HH:mm:ssx',
        display: 'yyyy-MM-dd\'T\'HH:mm:ss',
      },
      minute: {
        backend: '%Y-%m-%dT%H:%i+0100',
        chart: 'yyyy-MM-dd\'T\'HH:mmx',
        display: 'yyyy-MM-dd\'T\'HH:mm',
      },
      hour: {
        backend: '%Y-%m-%dT%H+0100',
        chart: 'yyyy-MM-dd\'T\'HHx',
        display: 'yyyy-MM-dd\'T\'HH',
      },
      day: {
        backend: '%Y-%m-%d+0100',
        chart: 'yyyy-MM-ddx',
        display: 'yyyy-MM-dd',
      },
      week: {
        backend: '%x-%v+0100',
        chart: 'RRRR-IIx',
        display: 'RRRR-II',
      },
      month: {
        backend: '%Y-%m+0100',
        chart: 'yyyy-MMx',
        display: 'yyyy-MM',
      },
      year: {
        backend: '%Y+0100',
        chart: 'yyyyx',
        display: 'yyyy',
      },
    };
    const groupFormat = ref(groupFormats.month);
    const customerGroupFormats = [GROUP_BY_CUSTOMER_GRPUP, GROUP_BY_CUSTOMER];
    const customerGroupFormat = ref(customerGroupFormats[0]);
    const lastFetchGroupFormat = ref(customerGroupFormats[0]);

    const {
      loadOptions: loadStoreOptions,
      loading: loadingStoreOptions,
    } = useLoadOptions('/stores', 'name');

    const {
      loadOptions: loadCustomerGroupOptions,
      loading: loadingCustomerGroupOptions,
    } = useLoadOptions('/customer_groups', 'name');

    const fetchData = async () => {
      const currentCustomerGroupingMode = customerGroupFormat.value;
      const groupedByCustomerGroups = currentCustomerGroupingMode === GROUP_BY_CUSTOMER_GRPUP;
      const { data } = await axios.post('/statistics/crm-promotions', {
        customerGroupingMode: customerGroupFormat.value,
        format: groupFormat.value.backend,
        start: start.value,
        end: end.value,
        stores: stores.value,
      });

      lastFetchGroupFormat.value = currentCustomerGroupingMode;

      statsData.value = {};
      timeSlots.value = [];
      vats.value = [];

      const discountIds = data.map((item) => item.discount, data);
      const promotionsIds = data.map((item) => item.promotion, data);
      const customerGroupIds = groupedByCustomerGroups ? data.map((item) => item.customerOrGroup, data) : [];
      allCurrencies.value = new Set(data.map((item) => item.currency, data));
      allVatRates.value = new Set(data.map((item) => item.vatRate, data));

      timeSlots.value = [...new Set(data.map((item) => item.slot))].sort();
      console.log(data);

      data.forEach((item) => {
        if (!Object.prototype.hasOwnProperty.call(statsData.value, item.slot)) {
          statsData.value[item.slot] = {
            rowCount: 0,
            entries: {},
          };
        }
        if (!Object.prototype.hasOwnProperty.call(statsData.value[item.slot].entries, item.customerOrGroup)) {
          statsData.value[item.slot].entries[item.customerOrGroup] = {
            rowCount: 0,
            entries: {},
          };
        }
        const key = `${item.slot}:${item.customerOrGroup}:${item.promotion}:${item.discount}`;
        if (!Object.prototype.hasOwnProperty.call(statsData.value[item.slot].entries[item.customerOrGroup].entries, key)) {
          statsData.value[item.slot].entries[item.customerOrGroup].entries[key] = {
            rowCount: 0,
            promotion: item.promotion,
            discount: item.discount,
            values: {},
          };

          statsData.value[item.slot].entries[item.customerOrGroup].entries[key].rowCount += 1;
          statsData.value[item.slot].entries[item.customerOrGroup].rowCount += 1;
          statsData.value[item.slot].rowCount += 1;
        }
        if (!Object.prototype.hasOwnProperty.call(statsData.value[item.slot].entries[item.customerOrGroup].entries[key].values, item.currency)) {
          statsData.value[item.slot].entries[item.customerOrGroup].entries[key].values[item.currency] = {};
        }
        statsData.value[item.slot].entries[item.customerOrGroup].entries[key].values[item.currency][item.vatRate] = item.amount;
      });

      if (customerGroupIds.length > 0) {
        const { data: customerGroupData } = await axios.get('/customer_groups', {
          params: {
            id: [...new Set(customerGroupIds.map((i) => getIdFromIri(i)))],
            pagination: false,
          },
        });
        customerGroups.value = {};
        customerGroupData['hydra:member'].forEach((customerGroup) => {
          customerGroups.value[customerGroup.id] = customerGroup;
        });
      }

      if (discountIds.length > 0) {
        const { data: discountData } = await axios.get('/discounts', {
          params: {
            id: [...new Set(discountIds.map((i) => getIdFromIri(i)))],
            pagination: false,
          },
        });
        discounts.value = {};
        discountData['hydra:member'].forEach((discount) => {
          discounts.value[discount.id] = discount;
        });
      }

      if (promotionsIds.length > 0) {
        const { data: promotionData } = await axios.get('/promotions', {
          params: {
            id: [...new Set(promotionsIds.map((i) => getIdFromIri(i)))],
            pagination: false,
          },
        });
        promotions.value = {};
        promotionData['hydra:member'].forEach((promotion) => {
          promotions.value[promotion.id] = promotion;
        });
      }
    };

    return {
      loadStoreOptions,
      loadingStoreOptions,
      stores,
      loadCustomerGroupOptions,
      loadingCustomerGroupOptions,
      start,
      end,
      groupFormat,
      groupFormats,
      fetchData,
      vats,
      timeSlots,
      statsData,
      formatPrice,
      parse,
      format,
      customerGroups,
      discounts,
      promotions,
      allCurrencies,
      allVatRates,
      customerGroupFormat,
      customerGroupFormats,
      lastFetchGroupFormat,
    };
  },
  computed: {
    startDate() {
      return formatISO(new Date(this.start));
    },
    endDate() {
      return formatISO(new Date(this.end));
    },
  },
};
</script>
<style lang="scss" scoped>
table {
  tr {
    &:hover {
      background-color: inherit;
    }
    &:nth-of-type(even):hover {
      background-color: var(--color-highlight);
    }
    td, th {
      padding: 1rem;
    }
  }
}
</style>
